import {type ConfigItem} from '@/api/config';

/** Groups a flat location list into roots and a parent → children index. */
// A row whose parent_id names something absent from the list is treated as a root rather than
// dropped, so a partially-loaded or mid-deprecation hierarchy still shows every location the analyst
// can pick instead of silently hiding a whole subtree.
export function buildLocationTree(locations: ConfigItem[]) {
  const byId = new Map(locations.map((l) => [l.id, l]));
  const childrenById = new Map<string, ConfigItem[]>();
  const roots: ConfigItem[] = [];

  for (const loc of locations) {
    const parentId = loc.parent_id;
    if (parentId && byId.has(parentId)) {
      const siblings = childrenById.get(parentId);
      if (siblings) {
        siblings.push(loc);
      } else {
        childrenById.set(parentId, [loc]);
      }
    } else {
      roots.push(loc);
    }
  }

  return {roots, childrenById};
}

/** Ancestor path per location ("Europe › Northern Europe"), for labelling search hits. */
export function buildLocationPaths(locations: ConfigItem[]) {
  const byId = new Map(locations.map((l) => [l.id, l]));
  const paths = new Map<string, string>();

  for (const loc of locations) {
    const ancestors: string[] = [];
    const seen = new Set<string>([loc.id]);
    let parentId = loc.parent_id;

    // `seen` guards against a cycle in the data rather than trusting it to be a tree.
    while (parentId && byId.has(parentId) && !seen.has(parentId)) {
      const parent = byId.get(parentId)!;
      ancestors.unshift(parent.name);
      seen.add(parentId);
      parentId = parent.parent_id;
    }

    if (ancestors.length > 0) {
      paths.set(loc.id, ancestors.join(' › '));
    }
  }

  return paths;
}

/** Left padding for a row at this depth, in pixels. */
// Tailwind can't express an arbitrary depth, so indentation is computed rather than classed.
export function indentFor(depth: number) {
  return BaseIndent + depth * DepthIndent;
}

const BaseIndent = 8;
const DepthIndent = 14;

/**
 A branch row is [chevron button][label button]. The chevron button is `pl-0.5 pr-1.5` around a
 12px icon (2 + 12 + 6 = 20px), and the label button adds `px-1` (4px) before its text. A leaf has
 neither, so it makes up both to align exactly with a sibling branch's text rather than sitting a
 few pixels to its left. Keep these in step with the classes in LocationNode.
 */
export const ChevronWidth = 20;
export const LabelPadding = 4;
